import {
  Activity,
  ArrowRight,
  Award,
  BookOpenText,
  Brain,
  Footprints,
  Heart,
  LifeBuoy,
  Medal,
  Moon,
  TrendingDown,
  TrendingUp,
  Trophy,
  UsersRound,
  Watch,
  Wind,
} from 'lucide-react'
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { Link } from 'react-router-dom'
import { useMemo } from 'react'
import { DataTable } from '@/components/shared/data-table'
import { SectionCard } from '@/components/shared/section-card'
import { StatusBadge } from '@/components/shared/status-badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { formatCompactNumber, formatDateTime, formatNumber, formatRelativeTime } from '@/lib/format'
import { useCollectionCount, useCollectionRecords } from '@/hooks'
import { usePlatformDailyStats, useStepsLeaderboard } from '@/hooks/useDashboardAggregates'
import { SupportTicket, UserProfile } from '@/types'
import { cn } from '@/lib/utils'

interface KpiCard {
  label: string
  value: string
  delta?: { value: number; positiveIsGood: boolean } | null
  icon: any
  color: string
  bgColor: string
}

export function OverviewPage() {
  const usersCount = useCollectionCount(['users'])
  const articlesCount = useCollectionCount(['articles'])
  const watchfacesCount = useCollectionCount(['watchfaces'])

  const platformStats = usePlatformDailyStats(14)
  const topPerformers = useStepsLeaderboard(7, 5)

  const recentUsers = useCollectionRecords<UserProfile>(['users'], {
    orderByField: 'lastSync',
    direction: 'desc',
    limitCount: 6,
  })
  const recentSyncedUsers = useCollectionRecords<UserProfile>(['users'], {
    orderByField: 'lastSync',
    direction: 'desc',
    limitCount: 8,
  })
  const recentTickets = useCollectionRecords<SupportTicket>(['supportTickets'], {
    orderByField: 'updatedAt',
    direction: 'desc',
    limitCount: 5,
  })

  const stats = platformStats.data
  const todayStat = stats.length > 0 ? stats[stats.length - 1] : null
  const yesterdayStat = stats.length > 1 ? stats[stats.length - 2] : null

  // KPIs
  const kpis = useMemo<KpiCard[]>(() => {
    const usersDelta = null

    const activeToday = todayStat?.activeUsers ?? 0
    const activeYesterday = yesterdayStat?.activeUsers ?? 0
    const activeDelta = activeYesterday > 0
      ? Math.round(((activeToday - activeYesterday) / activeYesterday) * 100)
      : null

    const workoutsThisWeek = stats.slice(-7).reduce((acc, s) => acc + s.workouts, 0)
    const workoutsPrevWeek = stats.slice(-14, -7).reduce((acc, s) => acc + s.workouts, 0)
    const workoutsDelta = workoutsPrevWeek > 0
      ? Math.round(((workoutsThisWeek - workoutsPrevWeek) / workoutsPrevWeek) * 100)
      : null

    const avgStepsToday = todayStat?.avgSteps ?? 0
    const avgStepsYesterday = yesterdayStat?.avgSteps ?? 0
    const stepsDelta = avgStepsYesterday > 0
      ? Math.round(((avgStepsToday - avgStepsYesterday) / avgStepsYesterday) * 100)
      : null

    return [
      {
        label: 'Total Users',
        value: formatCompactNumber(usersCount.data),
        delta: usersDelta,
        icon: UsersRound,
        color: 'text-orange-400',
        bgColor: 'bg-orange-500/10',
      },
      {
        label: 'Active Today',
        value: formatNumber(activeToday),
        delta: activeDelta != null ? { value: activeDelta, positiveIsGood: true } : null,
        icon: Activity,
        color: 'text-emerald-400',
        bgColor: 'bg-emerald-500/10',
      },
      {
        label: 'Workouts (7d)',
        value: formatNumber(workoutsThisWeek),
        delta: workoutsDelta != null ? { value: workoutsDelta, positiveIsGood: true } : null,
        icon: Award,
        color: 'text-amber-400',
        bgColor: 'bg-amber-500/10',
      },
      {
        label: 'Avg Steps Today',
        value: formatNumber(avgStepsToday),
        delta: stepsDelta != null ? { value: stepsDelta, positiveIsGood: true } : null,
        icon: Footprints,
        color: 'text-sky-400',
        bgColor: 'bg-sky-500/10',
      },
    ]
  }, [usersCount.data, todayStat, yesterdayStat, stats])

  // Health pulse averages over last 7 days
  const healthPulse = useMemo(() => {
    const last7 = stats.slice(-7).filter((d) => d.activeUsers > 0)
    const safeAvg = (vals: number[]) =>
      vals.length > 0 ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : 0
    return {
      avgHr: safeAvg(last7.map((d) => d.avgHr).filter((v) => v > 0)),
      avgSleep: safeAvg(last7.map((d) => d.avgSleep).filter((v) => v > 0)),
      avgSpo2: safeAvg(last7.map((d) => d.avgSpo2).filter((v) => v > 0)),
      avgStress: safeAvg(last7.map((d) => d.avgStress).filter((v) => v > 0)),
    }
  }, [stats])

  return (
    <div className="space-y-6">
      {/* HERO */}
      <section className="rounded-[36px] border border-stone-800 bg-[linear-gradient(135deg,#1c1c1c_0%,#0a0a0a_100%)] p-8 text-white shadow-2xl">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="inline-flex rounded-full border border-orange-500/20 bg-orange-500/10 px-4 py-1 text-xs uppercase tracking-[0.24em] text-orange-400">
              Overview
            </div>
            <h2 className="mt-5 max-w-2xl text-4xl font-semibold leading-tight text-stone-100">
              Selamat datang kembali, Admin.
            </h2>
            <p className="mt-3 max-w-2xl text-base leading-7 text-stone-400">
              Ringkasan ekosistem Parlent: kesehatan user, sinkronisasi smartwatch, konten CMS, dan tiket support.
            </p>
          </div>
          <div className="flex flex-col gap-2 text-right text-xs text-stone-500">
            <span>{new Date().toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
            <span className="text-stone-600">
              {watchfacesCount.data ?? 0} watchfaces · {formatCompactNumber(articlesCount.data)} articles
            </span>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {kpis.map((kpi) => (
            <div
              key={kpi.label}
              className="rounded-3xl border border-stone-800 bg-stone-900/60 p-5 backdrop-blur"
            >
              <div className="flex items-center justify-between">
                <div className={cn('rounded-2xl p-2.5', kpi.bgColor)}>
                  <kpi.icon className={cn('h-5 w-5', kpi.color)} />
                </div>
                {kpi.delta && (
                  <div
                    className={cn(
                      'flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold',
                      (kpi.delta.value >= 0) === kpi.delta.positiveIsGood
                        ? 'bg-emerald-500/10 text-emerald-400'
                        : 'bg-rose-500/10 text-rose-400',
                    )}
                  >
                    {kpi.delta.value >= 0 ? (
                      <TrendingUp className="h-3.5 w-3.5" />
                    ) : (
                      <TrendingDown className="h-3.5 w-3.5" />
                    )}
                    {kpi.delta.value > 0 ? '+' : ''}
                    {kpi.delta.value}%
                  </div>
                )}
              </div>
              <p className="mt-4 text-sm text-stone-400">{kpi.label}</p>
              <p className="mt-1 text-3xl font-bold text-white">{kpi.value}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CHART + TOP PERFORMERS */}
      <div className="grid gap-6 xl:grid-cols-3">
        <SectionCard
          title="Daily Activity (14 Days)"
          description="Avg steps & active users per day across all synced users."
          className="xl:col-span-2"
          action={
            <Link
              to="/analytics"
              className="text-sm font-semibold text-orange-500 hover:text-orange-400 inline-flex items-center gap-1"
            >
              Open Analytics <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          }
        >
          {stats.length > 0 ? (
            <div className="h-[300px] w-full">
              <ResponsiveContainer height="100%" width="100%">
                <AreaChart data={stats}>
                  <defs>
                    <linearGradient id="ovColorSteps" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f97316" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#f97316" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="ovColorUsers" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#262626" strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="date"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#737373', fontSize: 12 }}
                    dy={10}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#737373', fontSize: 12 }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#171717',
                      border: '1px solid #262626',
                      borderRadius: '16px',
                      color: '#fff',
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="avgSteps"
                    stroke="#f97316"
                    strokeWidth={3}
                    fillOpacity={1}
                    fill="url(#ovColorSteps)"
                    name="Avg Steps"
                  />
                  <Area
                    type="monotone"
                    dataKey="activeUsers"
                    stroke="#10b981"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#ovColorUsers)"
                    name="Active Users"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-stone-500">
              {platformStats.loading ? 'Aggregating data…' : 'No platform activity yet.'}
            </div>
          )}
        </SectionCard>

        <SectionCard
          title="Top Performers"
          description="Top 5 by total steps (7 days)"
          action={
            <Link
              to="/leaderboard"
              className="text-sm font-semibold text-orange-500 hover:text-orange-400 inline-flex items-center gap-1"
            >
              Full <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          }
        >
          {topPerformers.data.length > 0 ? (
            <div className="space-y-3">
              {topPerformers.data.map((entry) => (
                <Link
                  key={entry.uid}
                  to={`/users/${entry.uid}`}
                  className="flex items-center gap-3 rounded-2xl border border-stone-800 bg-stone-900/40 p-3 transition hover:bg-stone-800/60"
                >
                  <div
                    className={cn(
                      'flex h-7 w-7 items-center justify-center rounded-full text-sm font-bold',
                      entry.rank === 1 ? 'bg-amber-500/20 text-amber-400' :
                      entry.rank === 2 ? 'bg-stone-500/20 text-stone-300' :
                      entry.rank === 3 ? 'bg-orange-700/20 text-orange-400' :
                      'bg-stone-800 text-stone-400'
                    )}
                  >
                    {entry.rank === 1 ? <Trophy className="h-4 w-4" /> : entry.rank}
                  </div>
                  <Avatar className="h-9 w-9 border border-stone-700">
                    <AvatarImage src={entry.photoUrl ?? undefined} />
                    <AvatarFallback className="bg-stone-800 text-xs text-stone-400">
                      {entry.displayName.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-white">{entry.displayName}</p>
                    <p className="truncate text-xs text-stone-500">
                      {formatNumber(entry.totalSteps)} steps · {entry.daysActive}d
                    </p>
                  </div>
                  <Medal className="h-4 w-4 text-stone-600 shrink-0" />
                </Link>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <Trophy className="mb-3 h-10 w-10 text-stone-700" />
              <p className="text-sm text-stone-500">
                {topPerformers.loading ? 'Computing…' : 'No leaderboard data yet.'}
              </p>
            </div>
          )}
        </SectionCard>
      </div>

      {/* HEALTH PULSE */}
      <SectionCard
        title="Health Pulse — Platform Average (7 Days)"
        description="Aggregate vital signs across all users with synced data."
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <PulseTile
            icon={Heart}
            label="Avg Heart Rate"
            value={healthPulse.avgHr > 0 ? `${healthPulse.avgHr} bpm` : '--'}
            color="text-rose-400"
            bgColor="bg-rose-500/10"
            ringColor="ring-rose-500/20"
          />
          <PulseTile
            icon={Moon}
            label="Avg Sleep"
            value={healthPulse.avgSleep > 0 ? `${(healthPulse.avgSleep / 60).toFixed(1)} hrs` : '--'}
            color="text-indigo-400"
            bgColor="bg-indigo-500/10"
            ringColor="ring-indigo-500/20"
          />
          <PulseTile
            icon={Wind}
            label="Avg SpO2"
            value={healthPulse.avgSpo2 > 0 ? `${healthPulse.avgSpo2}%` : '--'}
            color="text-sky-400"
            bgColor="bg-sky-500/10"
            ringColor="ring-sky-500/20"
          />
          <PulseTile
            icon={Brain}
            label="Avg Stress"
            value={healthPulse.avgStress > 0 ? `${healthPulse.avgStress}/100` : '--'}
            subtitle={
              healthPulse.avgStress > 0
                ? healthPulse.avgStress <= 30 ? 'low' : healthPulse.avgStress <= 60 ? 'moderate' : 'high'
                : undefined
            }
            color="text-purple-400"
            bgColor="bg-purple-500/10"
            ringColor="ring-purple-500/20"
          />
        </div>
      </SectionCard>

      {/* RECENT USERS + SYNC ACTIVITY */}
      <div className="grid gap-6 xl:grid-cols-2">
        <SectionCard
          title="Recent Profiles"
          description="Users with the most recent sync activity."
          action={
            <Link to="/users" className="text-sm font-semibold text-orange-500 hover:text-orange-400 inline-flex items-center gap-1">
              All users <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          }
        >
          <DataTable
            rows={recentUsers.data ?? []}
            columns={[
              {
                header: 'User',
                render: (row) => {
                  const userId = row.id ?? row.uid
                  return (
                    <Link
                      to={userId ? `/users/${userId}` : '#'}
                      className="flex items-center gap-3"
                    >
                      <Avatar className="h-9 w-9 border border-stone-800">
                        <AvatarImage src={row.photoUrl || undefined} />
                        <AvatarFallback className="bg-stone-800 text-[10px] text-stone-400">
                          {row.displayName?.charAt(0) || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <p className="truncate font-semibold text-stone-100 hover:text-orange-400 transition">
                          {row.displayName || 'Unnamed'}
                        </p>
                        <p className="truncate text-xs text-stone-500">{row.email || userId}</p>
                      </div>
                    </Link>
                  )
                },
              },
              {
                header: 'Status',
                render: (row) => <StatusBadge value={row.status || 'active'} />,
              },
              {
                header: 'Last Sync',
                render: (row) => <span className="text-stone-400">{formatRelativeTime(row.lastSync)}</span>,
              },
            ]}
            emptyDescription="Belum ada user terdaftar."
            emptyTitle="Data Kosong"
          />
        </SectionCard>

        <SectionCard
          title="Sync Activity"
          description="Live feed of the most recent device syncs."
        >
          <div className="space-y-2">
            {(recentSyncedUsers.data ?? []).slice(0, 8).map((u, i) => {
              const userId = u.id ?? u.uid
              const lastSync: any = u.lastSync
              const stale = lastSync?.seconds
                ? Date.now() / 1000 - lastSync.seconds > 7 * 24 * 60 * 60
                : true
              return (
                <Link
                  key={userId ?? i}
                  to={userId ? `/users/${userId}` : '#'}
                  className="flex items-center gap-3 rounded-2xl border border-stone-800 bg-stone-900/40 p-3 transition hover:bg-stone-800/60"
                >
                  <div className={cn('h-2.5 w-2.5 rounded-full shrink-0', stale ? 'bg-stone-600' : 'bg-emerald-500 animate-pulse')} />
                  <Avatar className="h-8 w-8 border border-stone-700">
                    <AvatarImage src={u.photoUrl || undefined} />
                    <AvatarFallback className="bg-stone-800 text-[10px] text-stone-400">
                      {u.displayName?.charAt(0) || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-white">{u.displayName || 'Unnamed'}</p>
                    <p className="truncate text-xs text-stone-500">{u.email || userId}</p>
                  </div>
                  <span className={cn('text-xs whitespace-nowrap', stale ? 'text-stone-600' : 'text-stone-400')}>
                    {formatRelativeTime(u.lastSync)}
                  </span>
                </Link>
              )
            })}
            {(recentSyncedUsers.data ?? []).length === 0 && (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Watch className="mb-3 h-10 w-10 text-stone-700" />
                <p className="text-sm text-stone-500">No sync activity yet.</p>
              </div>
            )}
          </div>
        </SectionCard>
      </div>

      {/* QUICK LINKS */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <QuickLink
          to="/analytics"
          icon={Activity}
          label="Analytics"
          description="Platform-wide trends & insights"
          color="text-orange-400"
          bgColor="bg-orange-500/10"
        />
        <QuickLink
          to="/leaderboard"
          icon={Trophy}
          label="Leaderboard"
          description="Top performers ranking"
          color="text-amber-400"
          bgColor="bg-amber-500/10"
        />
        <QuickLink
          to="/users"
          icon={UsersRound}
          label="Users"
          description="Manage user profiles"
          color="text-emerald-400"
          bgColor="bg-emerald-500/10"
        />
        <QuickLink
          to="/content"
          icon={BookOpenText}
          label="Content"
          description="CMS articles & watchfaces"
          color="text-indigo-400"
          bgColor="bg-indigo-500/10"
        />
      </div>

      {/* SUPPORT TICKETS */}
      <SectionCard
        title="Support Signals"
        description="Tiket support dan bug report terbaru."
        action={
          <Link to="/support" className="text-sm font-semibold text-orange-500 hover:text-orange-400 inline-flex items-center gap-1">
            Go to support <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        }
      >
        <DataTable
          rows={recentTickets.data ?? []}
          columns={[
            {
              header: 'Issue',
              render: (row) => (
                <div className="flex items-start gap-3">
                  <LifeBuoy className="h-4 w-4 text-stone-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-semibold text-stone-100">{row.subject ?? 'Tanpa judul'}</p>
                    <p className="text-xs text-stone-500">{row.category ?? 'general'}</p>
                  </div>
                </div>
              ),
            },
            {
              header: 'Priority',
              render: (row) => <StatusBadge value={row.priority ?? 'medium'} />,
            },
            {
              header: 'Updated',
              render: (row) => <span className="text-stone-400">{formatDateTime(row.updatedAt)}</span>,
            },
          ]}
          emptyTitle="Belum ada tiket support"
          emptyDescription="Koleksi `supportTickets` akan tampil di sini."
        />
      </SectionCard>
    </div>
  )
}

function PulseTile({
  icon: Icon,
  label,
  value,
  subtitle,
  color,
  bgColor,
  ringColor,
}: {
  icon: any
  label: string
  value: string
  subtitle?: string
  color: string
  bgColor: string
  ringColor: string
}) {
  return (
    <div className={cn('rounded-3xl border border-stone-800 bg-stone-900/50 p-5 ring-1', ringColor)}>
      <div className="flex items-center gap-3">
        <div className={cn('rounded-2xl p-2.5', bgColor)}>
          <Icon className={cn('h-5 w-5', color)} />
        </div>
        <p className="text-sm text-stone-400">{label}</p>
      </div>
      <p className="mt-4 text-3xl font-bold text-white">{value}</p>
      {subtitle && <p className={cn('mt-1 text-xs uppercase tracking-wider', color)}>{subtitle}</p>}
    </div>
  )
}

function QuickLink({
  to,
  icon: Icon,
  label,
  description,
  color,
  bgColor,
}: {
  to: string
  icon: any
  label: string
  description: string
  color: string
  bgColor: string
}) {
  return (
    <Link
      to={to}
      className="group rounded-3xl border border-stone-800 bg-[#1a1a1a] p-5 transition hover:border-stone-700 hover:bg-stone-900/80"
    >
      <div className="flex items-center justify-between">
        <div className={cn('rounded-2xl p-2.5', bgColor)}>
          <Icon className={cn('h-5 w-5', color)} />
        </div>
        <ArrowRight className="h-4 w-4 text-stone-600 transition group-hover:translate-x-1 group-hover:text-orange-400" />
      </div>
      <p className="mt-4 text-base font-bold text-white">{label}</p>
      <p className="mt-1 text-xs text-stone-500">{description}</p>
    </Link>
  )
}

